import type { FC, ReactNode } from 'react'
import type { JourneyArt } from '@/types'

import { useId } from 'react'
import { cn } from '@/lib/utils'
import CrestMark from '@/components/landing/crest-mark'

import {
    CREST_HEAD_OFFSET_Y,
    crestEars,
    crestEyeLids,
    crestFace,
    crestForelock,
    crestPupils,
    crestWool
} from '@/lib/crest'

// Cut-paper instrument art. Each piece is ink on paper, cut out with a white margin
// and a hard paper shadow (the sticker filter), standing in for photo cutouts.

const INK = '#0D0D0D'
const PAPER = '#F1EFEA'

interface ArtProps {
    className?: string
    flat?: boolean
    animated?: boolean
}

interface InstrumentArtProps extends ArtProps {
    kind: JourneyArt
    label?: string
}

interface FilterProps {
    id: string
    spread?: number
    shadow?: number
}

const fretPositions = (nut: number, scale: number, count: number): number[] => {
    return Array.from({ length: count }, (_, index) => nut + scale * (1 - Math.pow(2, -(index + 1) / 12)))
}

const between = (frets: number[], nut: number, fret: number): number => {
    const previous = fret === 1 ? nut : frets[fret - 2]
    return (previous + frets[fret - 1]) / 2
}

const StickerFilter: FC<FilterProps> = ({ id, spread = 5, shadow = 6 }): ReactNode => (
    <filter id={id} x='-20%' y='-20%' width='140%' height='140%' colorInterpolationFilters='sRGB'>
        <feMorphology in='SourceAlpha' operator='dilate' radius={spread} result='grown' />
        <feFlood floodColor='#FFFFFF' result='paper' />
        <feComposite in='paper' in2='grown' operator='in' result='outline' />
        <feOffset in='grown' dx={shadow} dy={shadow} result='offset' />
        <feFlood floodColor={INK} floodOpacity='0.9' result='ink' />
        <feComposite in='ink' in2='offset' operator='in' result='shadow' />
        <feMerge>
            <feMergeNode in='shadow' />
            <feMergeNode in='outline' />
            <feMergeNode in='SourceGraphic' />
        </feMerge>
    </filter>
)

const useArtIds = (): { filter: string, extra: string } => {
    const id = useId().replace(/:/g, '')
    return { filter: `sticker-${id}`, extra: `art-${id}` }
}

const Strings: FC<{ count: number, nutY: number, nutX: [number, number], endY: number, endX: [number, number] }> = ({ count, nutY, nutX, endY, endX }): ReactNode => (
    <g stroke={PAPER} strokeWidth='0.9' opacity='0.7'>
        {Array.from({ length: count }, (_, index) => {
            const t = count === 1 ? 0.5 : index / (count - 1)
            return (
                <line
                    key={index}
                    x1={nutX[0] + (nutX[1] - nutX[0]) * t}
                    y1={nutY}
                    x2={endX[0] + (endX[1] - endX[0]) * t}
                    y2={endY}
                />
            )
        })}
    </g>
)

const LesPaul: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()
    const frets = fretPositions(98, 419.8, 22)
    const neckLeft = (y: number) => 106 - ((y - 98) / 306) * 5
    const neckRight = (y: number) => 134 + ((y - 98) / 306) * 5

    return (
        <svg viewBox='0 0 240 660' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
                <mask id={ids.extra} maskUnits='userSpaceOnUse' x='0' y='0' width='240' height='660'>
                    <rect width='240' height='660' fill='#FFFFFF' />
                    <circle cx='172' cy='388' r='30' fill='#000000' />
                </mask>
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <g fill={INK} mask={`url(#${ids.extra})`}>
                    <path d='M 92 98 L 84 24 Q 84 8 100 10 L 120 20 L 140 10 Q 156 8 156 24 L 148 98 Z' />
                    {[30, 54, 78].map(y => (
                        <g key={y}>
                            <rect x='72' y={y} width='14' height='8' />
                            <circle cx='70' cy={y + 4} r='6' />
                            <rect x='154' y={y} width='14' height='8' />
                            <circle cx='170' cy={y + 4} r='6' />
                        </g>
                    ))}
                    <polygon points='106,98 134,98 139,404 101,404' />
                    <ellipse cx='114' cy='448' rx='74' ry='64' />
                    <ellipse cx='120' cy='552' rx='98' ry='86' />
                </g>

                <g stroke={PAPER} strokeWidth='1.2' opacity='0.55'>
                    {frets.map(y => (
                        <line key={y} x1={neckLeft(y)} y1={y} x2={neckRight(y)} y2={y} />
                    ))}
                </g>

                <g fill={PAPER}>
                    {[3, 5, 7, 9, 12, 15, 17, 19, 21].map(fret => (
                        <rect key={fret} x='113' y={between(frets, 98, fret) - 3} width='14' height='6' />
                    ))}
                    <path d='M 146 424 Q 178 414 184 452 Q 188 494 160 516 L 146 516 Z' />
                    <rect x='98' y='452' width='44' height='24' />
                    <rect x='98' y='520' width='44' height='24' />
                    <rect x='94' y='560' width='52' height='6' />
                    <rect x='92' y='584' width='56' height='10' rx='5' />
                    {[[166, 576], [188, 560], [170, 606], [192, 590]].map(([x, y]) => (
                        <circle key={`${x}-${y}`} cx={x} cy={y} r='8' />
                    ))}
                    <circle cx='66' cy='428' r='6' />
                    <rect x='108' y='34' width='24' height='4' />
                </g>

                <g fill={INK}>
                    {Array.from({ length: 6 }, (_, index) => (
                        <g key={index}>
                            <circle cx={104 + index * 6.4} cy='464' r='2.2' />
                            <circle cx={104 + index * 6.4} cy='532' r='2.2' />
                        </g>
                    ))}
                    {[[166, 576], [188, 560], [170, 606], [192, 590]].map(([x, y]) => (
                        <circle key={`${x}-${y}-dot`} cx={x} cy={y} r='2' />
                    ))}
                </g>

                <line x1='66' y1='428' x2='58' y2='414' stroke={PAPER} strokeWidth='3' strokeLinecap='round' />

                <Strings count={6} nutY={98} nutX={[111, 129]} endY={588} endX={[103, 137]} />
            </g>
        </svg>
    )
}

const Telecaster: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()
    const frets = fretPositions(100, 415.5, 21)
    const neckLeft = (y: number) => 108 - ((y - 100) / 296) * 5
    const neckRight = (y: number) => 132 + ((y - 100) / 296) * 5

    return (
        <svg viewBox='0 0 240 660' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <g fill={INK}>
                    {[16, 28, 40, 52, 64, 76].map(y => (
                        <g key={y}>
                            <rect x='92' y={y} width='14' height='6' />
                            <circle cx='90' cy={y + 3} r='5' />
                        </g>
                    ))}
                    <path d='M 102 384 L 62 384 Q 34 384 32 414 L 30 468 Q 30 488 20 504 Q 8 526 10 566 Q 14 630 86 632 L 154 632 Q 226 630 230 566 Q 232 526 220 504 Q 210 488 210 468 Q 208 446 184 446 Q 156 446 146 432 L 138 384 Z' />
                </g>

                <path d='M 106 100 L 104 18 Q 104 8 116 8 L 146 12 Q 156 14 154 26 L 148 66 Q 146 78 136 84 L 134 100 Z' fill={PAPER} stroke={INK} strokeWidth='2.5' strokeLinejoin='round' />
                <polygon points='108,100 132,100 137,396 103,396' fill={PAPER} stroke={INK} strokeWidth='2.5' strokeLinejoin='round' />
                <rect x='116' y='22' width='26' height='4' fill={INK} transform='rotate(4 129 24)' />

                <g stroke={INK} strokeWidth='1.3' opacity='0.8'>
                    {frets.map(y => (
                        <line key={y} x1={neckLeft(y)} y1={y} x2={neckRight(y)} y2={y} />
                    ))}
                </g>

                <g fill={INK}>
                    {[3, 5, 7, 9, 15, 17, 19, 21].map(fret => (
                        <circle key={fret} cx='120' cy={between(frets, 100, fret)} r='3' />
                    ))}
                    <circle cx='114' cy={between(frets, 100, 12)} r='3' />
                    <circle cx='126' cy={between(frets, 100, 12)} r='3' />
                </g>

                <path d='M 101 398 L 64 398 Q 44 400 42 426 L 42 494 Q 46 522 80 530 L 112 532 Q 120 520 120 506 L 142 470 L 141 430 L 137 398 Z' fill={PAPER} />
                <rect x='105' y='440' width='32' height='18' rx='3' fill={PAPER} stroke={INK} strokeWidth='2' />
                <line x1='108' y1='445' x2='134' y2='445' stroke={INK} strokeWidth='1' opacity='0.5' />

                <rect x='88' y='548' width='64' height='48' rx='3' fill={PAPER} stroke={INK} strokeWidth='2' />
                <g transform='rotate(6 120 564)'>
                    <rect x='98' y='556' width='44' height='16' fill={INK} />
                    {Array.from({ length: 6 }, (_, index) => (
                        <circle key={index} cx={103 + index * 6.8} cy='564' r='2' fill={PAPER} />
                    ))}
                </g>
                {[96, 113, 130].map(x => (
                    <rect key={x} x={x} y='582' width='14' height='6' fill={INK} />
                ))}

                <g transform='rotate(-38 182 560)'>
                    <rect x='146' y='551' width='72' height='18' rx='9' fill={PAPER} />
                    <circle cx='160' cy='560' r='6' fill={INK} />
                    <circle cx='182' cy='560' r='6' fill={INK} />
                    <rect x='198' y='556' width='12' height='8' fill={INK} />
                </g>

                <g stroke={INK} strokeWidth='0.9' opacity='0.55'>
                    {Array.from({ length: 6 }, (_, index) => (
                        <line
                            key={index}
                            x1={112 + index * 3.2}
                            y1='100'
                            x2={101 + index * 7.6}
                            y2='584'
                        />
                    ))}
                </g>
            </g>
        </svg>
    )
}

const Bass: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()
    const frets = fretPositions(124, 519.7, 20)
    const neckLeft = (y: number) => 108 - ((y - 124) / 362) * 4
    const neckRight = (y: number) => 134 + ((y - 124) / 362) * 6

    return (
        <svg viewBox='0 0 240 780' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <g fill={INK}>
                    <path d='M 106 124 L 104 26 Q 104 12 118 12 L 148 16 Q 160 20 158 36 L 152 96 Q 150 110 140 116 L 136 124 Z' />
                    {[30, 54, 78, 102].map(y => (
                        <g key={y}>
                            <rect x='90' y={y - 3} width='16' height='6' />
                            <ellipse cx='82' cy={y} rx='11' ry='8' />
                        </g>
                    ))}
                    <polygon points='108,124 134,124 140,486 104,486' />
                    <path d='M 104 470 L 90 470 Q 76 412 50 416 Q 30 422 34 458 Q 38 492 30 512 Q 10 542 12 602 Q 18 688 110 692 Q 214 688 226 612 Q 234 560 212 532 Q 198 514 204 490 Q 210 458 194 448 Q 180 442 170 458 Q 158 482 142 482 L 140 470 Z' />
                </g>

                <g stroke={PAPER} strokeWidth='1.2' opacity='0.5'>
                    {frets.map(y => (
                        <line key={y} x1={neckLeft(y)} y1={y} x2={neckRight(y)} y2={y} />
                    ))}
                </g>

                <g fill={PAPER}>
                    {[3, 5, 7, 9, 15, 17, 19].map(fret => (
                        <circle key={fret} cx='121' cy={between(frets, 124, fret)} r='3' />
                    ))}
                    <circle cx='115' cy={between(frets, 124, 12)} r='3' />
                    <circle cx='127' cy={between(frets, 124, 12)} r='3' />
                    <path d='M 104 488 L 70 490 Q 46 496 46 530 L 48 610 Q 62 642 104 640 L 108 572 L 152 566 L 160 520 Q 166 498 150 488 Z' />
                    <rect x='94' y='648' width='56' height='18' />
                    <g transform='rotate(-28 184 622)'>
                        <rect x='154' y='610' width='60' height='24' rx='12' />
                    </g>
                </g>

                <g fill={INK}>
                    <rect x='80' y='548' width='30' height='14' />
                    <rect x='114' y='564' width='30' height='14' />
                    {[100, 113, 126, 139].map(x => (
                        <rect key={x} x={x - 4} y='652' width='8' height='10' />
                    ))}
                    <g transform='rotate(-28 184 622)'>
                        <circle cx='170' cy='622' r='7' />
                        <circle cx='198' cy='622' r='7' />
                    </g>
                </g>

                <g fill={PAPER}>
                    <circle cx='88' cy='555' r='2' />
                    <circle cx='102' cy='555' r='2' />
                    <circle cx='122' cy='571' r='2' />
                    <circle cx='136' cy='571' r='2' />
                </g>

                <Strings count={4} nutY={124} nutX={[113, 129]} endY={656} endX={[100, 139]} />
            </g>
        </svg>
    )
}

const SheepHead: FC<{ ink: string, paper: string }> = ({ ink, paper }): ReactNode => (
    <g transform={`translate(0 ${CREST_HEAD_OFFSET_Y})`}>
        {crestEars.map(path => (
            <path key={path} d={path} fill={ink} />
        ))}
        {crestWool.map(([x, y, r]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={ink} />
        ))}
        <path d={crestFace} fill={paper} stroke={ink} strokeWidth='7' />
        {crestForelock.map(([x, y, r]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={ink} />
        ))}
        {crestEyeLids.map(([x1, y1, x2, y2]) => (
            <line key={`${x1}-${y1}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={ink} strokeWidth='6' strokeLinecap='round' />
        ))}
        {crestPupils.map(path => (
            <path key={path} d={path} fill={ink} />
        ))}
    </g>
)

const Stick: FC<{ x1: number, y1: number, x2: number, y2: number }> = ({ x1, y1, x2, y2 }): ReactNode => (
    <g strokeLinecap='round'>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={INK} strokeWidth='10' />
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={PAPER} strokeWidth='5.5' />
    </g>
)

const DrumKit: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()

    return (
        <svg viewBox='0 0 520 440' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <g stroke={INK} strokeWidth='5' strokeLinecap='round' fill='none'>
                    <line x1='150' y1='70' x2='176' y2='170' />
                    <line x1='420' y1='104' x2='438' y2='236' />
                    <line x1='56' y1='156' x2='56' y2='416' />
                    <line x1='56' y1='378' x2='26' y2='422' />
                    <line x1='56' y1='378' x2='86' y2='422' />
                    <line x1='118' y1='286' x2='118' y2='378' />
                    <line x1='118' y1='378' x2='86' y2='424' />
                    <line x1='118' y1='378' x2='150' y2='424' />
                    <line x1='262' y1='216' x2='209' y2='206' strokeWidth='7' />
                    <line x1='262' y1='216' x2='321' y2='204' strokeWidth='7' />
                    <line x1='184' y1='384' x2='158' y2='424' />
                    <line x1='340' y1='384' x2='366' y2='424' />
                    <line x1='406' y1='322' x2='400' y2='422' />
                    <line x1='486' y1='322' x2='492' y2='422' />
                </g>

                <g fill={INK}>
                    <ellipse cx='150' cy='64' rx='66' ry='9' transform='rotate(-14 150 64)' />
                    <ellipse cx='420' cy='96' rx='80' ry='10' transform='rotate(10 420 96)' />
                    <ellipse cx='56' cy='150' rx='46' ry='7' />
                    <ellipse cx='56' cy='160' rx='46' ry='7' />
                </g>
                <g fill={PAPER}>
                    <ellipse cx='150' cy='64' rx='10' ry='3.5' transform='rotate(-14 150 64)' />
                    <ellipse cx='420' cy='96' rx='12' ry='3.5' transform='rotate(10 420 96)' />
                    <ellipse cx='56' cy='150' rx='8' ry='2.5' />
                </g>

                <g>
                    <rect x='170' y='150' width='78' height='56' fill={INK} />
                    <ellipse cx='209' cy='206' rx='39' ry='11' fill={INK} />
                    <ellipse cx='209' cy='150' rx='39' ry='11' fill={PAPER} stroke={INK} strokeWidth='3' />
                    <rect x='278' y='142' width='86' height='62' fill={INK} />
                    <ellipse cx='321' cy='204' rx='43' ry='12' fill={INK} />
                    <ellipse cx='321' cy='142' rx='43' ry='12' fill={PAPER} stroke={INK} strokeWidth='3' />
                    {[184, 209, 234].map(x => (
                        <rect key={x} x={x - 2} y='166' width='4' height='28' fill={PAPER} />
                    ))}
                    {[294, 321, 348].map(x => (
                        <rect key={x} x={x - 2} y='160' width='4' height='32' fill={PAPER} />
                    ))}
                </g>

                <g>
                    <rect x='398' y='232' width='96' height='90' fill={INK} />
                    <ellipse cx='446' cy='322' rx='48' ry='13' fill={INK} />
                    <ellipse cx='446' cy='232' rx='48' ry='13' fill={PAPER} stroke={INK} strokeWidth='3' />
                    {[414, 446, 478].map(x => (
                        <rect key={x} x={x - 2} y='252' width='4' height='56' fill={PAPER} />
                    ))}
                </g>

                <g>
                    <rect x='70' y='236' width='96' height='40' fill={INK} />
                    <ellipse cx='118' cy='276' rx='48' ry='13' fill={INK} />
                    <ellipse cx='118' cy='236' rx='48' ry='13' fill={PAPER} stroke={INK} strokeWidth='3' />
                    {[86, 118, 150].map(x => (
                        <rect key={x} x={x - 2} y='250' width='4' height='20' fill={PAPER} />
                    ))}
                </g>

                <g>
                    <circle cx='262' cy='318' r='104' fill={INK} />
                    <circle cx='262' cy='318' r='95' fill='none' stroke={PAPER} strokeWidth='3' />
                    {Array.from({ length: 10 }, (_, index) => {
                        const angle = (index / 10) * 360
                        const rad = (angle * Math.PI) / 180
                        const x = 262 + Math.cos(rad) * 100
                        const y = 318 + Math.sin(rad) * 100
                        return (
                            <rect
                                key={index}
                                x='-4'
                                y='-8'
                                width='8'
                                height='16'
                                fill={PAPER}
                                transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${angle + 90})`}
                            />
                        )
                    })}
                    <g transform='translate(262 316) scale(0.3) translate(-256 -256)'>
                        <SheepHead ink={PAPER} paper={INK} />
                    </g>
                </g>

                <Stick x1={150} y1={426} x2={286} y2={300} />
                <Stick x1={148} y1={302} x2={290} y2={420} />
            </g>
        </svg>
    )
}

const Mic: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()

    return (
        <svg viewBox='0 0 200 520' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
                <clipPath id={ids.extra}>
                    <rect x='64' y='24' width='72' height='124' rx='36' />
                </clipPath>
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <path d='M 100 204 C 100 244 152 252 152 304 C 152 364 58 382 62 440 C 64 468 118 472 142 486' fill='none' stroke={INK} strokeWidth='4' strokeLinecap='round' />
                <line x1='100' y1='208' x2='100' y2='474' stroke={INK} strokeWidth='8' strokeLinecap='round' />
                <ellipse cx='100' cy='486' rx='70' ry='16' fill={INK} />
                <path d='M 44 484 Q 100 470 156 484' fill='none' stroke={PAPER} strokeWidth='2' opacity='0.6' />
                <rect x='88' y='194' width='24' height='22' fill={INK} />
                <path d='M 82 140 L 118 140 L 112 202 L 88 202 Z' fill={INK} />
                <rect x='64' y='24' width='72' height='124' rx='36' fill={INK} />
                <g clipPath={`url(#${ids.extra})`} stroke={PAPER} strokeWidth='1.6' opacity='0.55'>
                    {Array.from({ length: 13 }, (_, index) => (
                        <line key={`h${index}`} x1='60' y1={34 + index * 8} x2='140' y2={34 + index * 8} />
                    ))}
                    {Array.from({ length: 9 }, (_, index) => (
                        <line key={`v${index}`} x1={68 + index * 8} y1='20' x2={68 + index * 8} y2='150' />
                    ))}
                </g>
                <rect x='64' y='84' width='72' height='9' fill={PAPER} />
                <rect x='86' y='150' width='28' height='4' fill={PAPER} opacity='0.7' />
            </g>
        </svg>
    )
}

const Amp: FC<ArtProps> = ({ className, flat }): ReactNode => {
    const ids = useArtIds()

    return (
        <svg viewBox='0 0 380 360' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
                <pattern id={ids.extra} width='7' height='7' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
                    <rect width='7' height='7' fill={INK} />
                    <line x1='0' y1='0' x2='0' y2='7' stroke={PAPER} strokeWidth='1.6' opacity='0.42' />
                </pattern>
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <rect x='150' y='10' width='80' height='22' rx='11' fill={INK} />
                <rect x='16' y='28' width='348' height='316' rx='10' fill={INK} />
                <rect x='36' y='46' width='308' height='46' fill={PAPER} />
                {Array.from({ length: 7 }, (_, index) => {
                    const x = 66 + index * 34
                    const angle = [-120, -60, 0, 40, -90, 70, -20][index]
                    return (
                        <g key={index}>
                            <circle cx={x} cy='69' r='10' fill={INK} />
                            <line x1={x} y1='69' x2={x} y2='61' stroke={PAPER} strokeWidth='2.4' strokeLinecap='round' transform={`rotate(${angle} ${x} 69)`} />
                        </g>
                    )
                })}
                <circle cx='320' cy='69' r='8' fill='none' stroke={INK} strokeWidth='3' />
                <circle cx='320' cy='69' r='3.5' fill={INK} />
                <rect x='36' y='108' width='308' height='218' fill={`url(#${ids.extra})`} />
                <rect x='36' y='108' width='308' height='218' fill='none' stroke={PAPER} strokeWidth='3' />
                <g fill='none' stroke={PAPER} strokeWidth='2' opacity='0.35'>
                    <circle cx='130' cy='220' r='64' />
                    <circle cx='250' cy='220' r='64' />
                    <circle cx='130' cy='220' r='20' />
                    <circle cx='250' cy='220' r='20' />
                </g>
                <rect x='50' y='120' width='84' height='22' fill={PAPER} />
                <text
                    x='92'
                    y='135.5'
                    fill={INK}
                    fontSize='12'
                    fontWeight='700'
                    textAnchor='middle'
                    letterSpacing='3'
                    style={{ fontFamily: 'var(--font-app-mono, ui-monospace, monospace)' }}
                >
                    BLUNT
                </text>
                <g fill={PAPER}>
                    <polygon points='16,28 44,28 16,56' />
                    <polygon points='364,28 336,28 364,56' />
                    <polygon points='16,344 44,344 16,316' />
                    <polygon points='364,344 336,344 364,316' />
                </g>
            </g>
        </svg>
    )
}

const SEQUENCER_PATTERN = [
    [0, 4, 8, 10, 12],
    [4, 12],
    [0, 2, 4, 6, 8, 10, 12, 14],
    [0, 3, 6, 7, 11, 14]
]

const Sequencer: FC<ArtProps> = ({ className, flat, animated }): ReactNode => {
    const ids = useArtIds()
    const step = 26.25

    return (
        <svg viewBox='0 0 480 300' className={className} aria-hidden='true'>
            <defs>
                <StickerFilter id={ids.filter} />
            </defs>

            <g filter={flat ? undefined : `url(#${ids.filter})`}>
                <rect x='12' y='24' width='456' height='252' rx='12' fill={INK} />
                <rect x='36' y='46' width='128' height='44' fill={PAPER} />
                <text
                    x='48'
                    y='74'
                    fill={INK}
                    fontSize='15'
                    fontWeight='700'
                    letterSpacing='2'
                    style={{ fontFamily: 'var(--font-app-mono, ui-monospace, monospace)' }}
                >
                    BPM 150
                </text>
                {Array.from({ length: 5 }, (_, index) => {
                    const x = 214 + index * 50
                    const angle = [-130, -40, 30, 100, -80][index]
                    return (
                        <g key={index}>
                            <circle cx={x} cy='68' r='13' fill={PAPER} />
                            <line x1={x} y1='68' x2={x} y2='58' stroke={INK} strokeWidth='3' strokeLinecap='round' transform={`rotate(${angle} ${x} 68)`} />
                        </g>
                    )
                })}

                {SEQUENCER_PATTERN.map((lit, row) => (
                    <g key={row}>
                        {Array.from({ length: 16 }, (_, column) => {
                            const on = lit.includes(column)
                            return (
                                <rect
                                    key={column}
                                    x={36 + column * step}
                                    y={112 + row * 30}
                                    width='20'
                                    height='20'
                                    fill={on ? PAPER : 'none'}
                                    stroke={PAPER}
                                    strokeWidth='1.6'
                                    opacity={on ? 1 : 0.45}
                                />
                            )
                        })}
                    </g>
                ))}

                <rect
                    x='33'
                    y='106'
                    width='26'
                    height='122'
                    fill={PAPER}
                    opacity='0.2'
                    className={cn(animated && 'seq-playhead')}
                />

                {Array.from({ length: 16 }, (_, column) => (
                    <rect key={column} x={42 + column * step} y='240' width='8' height='4' fill={PAPER} opacity={column % 4 === 0 ? 0.9 : 0.35} />
                ))}

                <rect x='36' y='252' width='26' height='14' fill={PAPER} />
                <polygon points='45,255 45,263 54,259' fill={INK} />
                <rect x='68' y='252' width='26' height='14' fill={PAPER} />
                <rect x='77' y='255' width='8' height='8' fill={INK} />
            </g>
        </svg>
    )
}

const InstrumentArt: FC<InstrumentArtProps> = ({ kind, className, flat, animated, label }): ReactNode => {
    const shared = { className: cn('block h-auto w-full', className), flat, animated }

    let art: ReactNode = null

    if (kind === 'les-paul') art = <LesPaul {...shared} />
    if (kind === 'telecaster') art = <Telecaster {...shared} />
    if (kind === 'bass') art = <Bass {...shared} />
    if (kind === 'drum-kit') art = <DrumKit {...shared} />
    if (kind === 'mic') art = <Mic {...shared} />
    if (kind === 'amp') art = <Amp {...shared} />
    if (kind === 'sequencer') art = <Sequencer {...shared} />
    if (kind === 'crest') art = <CrestMark className={shared.className} />

    if (!label) return art

    return (
        <figure role='img' aria-label={label} className='m-0'>
            {art}
        </figure>
    )
}

export default InstrumentArt
